/**
 * Taboola Pixel — self-registering. Yahoo variant when `account_type === 'yahoo'`
 * loads `ytc.js` (same `_tfa` global).
 */

import { warn } from '../../util/log';
import type { TaboolaPixel } from '../../core/config/types';
import { on, SdkEvent } from '../../core/events/bus';
import type { PixelRegister } from './types';

declare global {
  interface Window {
    _tfa?: unknown[];
  }
}

const TABOOLA_URL = '//cdn.taboola.com/libtrc/unip/{ID}/tfa.js';
const YAHOO_URL = '//s.yimg.com/wi/ytc.js';

let scriptInjectedFor = '';

function ensureTfa(pixel: TaboolaPixel): void {
  if (typeof window === 'undefined') return;
  if (!Array.isArray(window._tfa)) window._tfa = [];

  const url =
    pixel.account_type === 'yahoo' ? YAHOO_URL : TABOOLA_URL.replace('{ID}', pixel.pixel_id);
  if (scriptInjectedFor === url) return;
  scriptInjectedFor = url;
  try {
    const s = document.createElement('script');
    s.async = true;
    s.src = url;
    (document.head || document.documentElement).appendChild(s);
  } catch (err) {
    warn('taboola script inject failed', err);
  }
}

// `_tfa` pushes carry the pixel id, so multi-pixel setups stay scoped.
export const registerTaboola: PixelRegister<TaboolaPixel> = (pixel, context) => {
  on(SdkEvent.PAGE_VIEW, () => {
    if (context.getConsent() !== 'granted') return;
    try {
      ensureTfa(pixel);
      window._tfa!.push({ notify: 'event', name: 'page_view', id: pixel.pixel_id });
    } catch (err) {
      warn('taboola pageview failed', err);
    }
  });

  on(SdkEvent.ECOM_CLICKOUT, () => {
    if (context.getConsent() !== 'granted') return;
    try {
      ensureTfa(pixel);
      window._tfa!.push({ notify: 'event', name: 'lead-fixed', id: pixel.pixel_id });
    } catch (err) {
      warn('taboola clickout failed', err);
    }
  });

  // whole cents 1..40 (yoto parity)
  on(SdkEvent.SESSION_BID_CENT_INCREMENT, (cents) => {
    if (context.getConsent() !== 'granted') return;
    if (cents <= 0 || cents > 40 || cents % 1 !== 0) return;
    try {
      ensureTfa(pixel);
      window._tfa!.push({ notify: 'event', name: `${cents}_cent`, id: pixel.pixel_id });
    } catch (err) {
      warn('taboola cent event failed', err);
    }
  });
};

export function _resetTaboolaForTests(): void {
  scriptInjectedFor = '';
}
